import { inject, Injectable } from '@angular/core';
import { EnvService } from '../../env.service';

@Injectable({
  providedIn: 'root',
})
export class ApiConstants {
  private envService = inject(EnvService);

  OAUTH_TOKEN() {
    return `${this.envService?.ApiUrl}/uaa/oauth/token`;
  }

  PAYMENT_INFO(orderId: string) {
    return `${this.envService?.ApiUrl}/accounts/payment/info/${orderId}`;
  }

  STANDALONE_PAYMENT_INFO(paymentToken: string) {
    return `${this.envService?.ApiUrl}/accounts/api/payment-links/${paymentToken}`;
  }

  // Sent with the customer's token when logged in; the backend prices plans for that customer.
  PAYMENT_PLAN(orderId: string) {
    return `${this.envService?.ApiUrl}/accounts/payment/plans/v2/${orderId}`;
  }

  CANCEL_ORDER_REQUEST(orderId: string) {
    return `${this.envService?.ApiUrl}/accounts/payment/cancel/${orderId}`;
  }

  CNET_SOURCE_VALIDATION(amount: string | number, orderId: string, orderToken?: string | null) {
    const tokenParam =
      typeof orderToken === 'string' && orderToken
        ? `&orderToken=${encodeURIComponent(orderToken)}`
        : '';
    return `${this.envService?.ApiUrl}/accounts/credit-validation/validate?amount=${amount}&orderId=${orderId}${tokenParam}`;
  }
  CNET_AUTHENTICATION_REQUEST(requestId: string | number) {
    return `${this.envService?.ApiUrl}/accounts/credit-validation/validate/status?requestId=${requestId}`;
  }
  VERIFY_OTP() {
    return `${this.envService?.ApiUrl}/accounts/signup/verifyOtp`;
  }

  RESEND_OTP() {
    return `${this.envService?.ApiUrl}/accounts/signup/resendOtp`;
  }

  INIT_AUTH() {
    return `${this.envService?.ApiUrl}/accounts/signup/init`;
  }

  NATIONAL_ID() {
    return `${this.envService?.ApiUrl}/accounts/signup/verifyNationalId`;
  }

  CNET_AUTHENTICATION() {
    return `${this.envService?.ApiUrl}/accounts/signup/cinet/authentication`;
  }

  CNET_AUTHENTICATION_STATUS(civilId: string, referenceNo: string) {
    return `${this.envService?.ApiUrl}/accounts/signup/cinet/authentication/status?civilId=${civilId}&referenceNo=${referenceNo}`;
  }

  CNET_AUTHENTICATION_TOKEN(requestId: string, userId: string) {
    return `${this.envService?.ApiUrl}/accounts/signup/token/otp?requestId=${requestId}&userId=${userId}`;
  }

  REQUEST_STATUS() {
    return `${this.envService?.ApiUrl}/accounts/signup/nationalId/status`;
  }

  CUSTOMER_INFO() {
    return `${this.envService?.ApiUrl}/accounts/signup/customerInfo`;
  }

  CREDIT_CARD() {
    return `${this.envService?.ApiUrl}/accounts/profile/creditCard`;
  }

  ORDER_PROCESS() {
    return `${this.envService?.ApiUrl}/accounts/payment/processOrderPayment`;
  }

  INSTALLMENT_PAY_KNET() {
    return `${this.envService?.ApiUrl}/accounts/payment/v2/payInstallment/knet`;
  }

  STANDALONE_INSTALLMENT_PAY_KNET(paymentToken: string) {
    return `${this.envService?.ApiUrl}/accounts/api/payment-links/${paymentToken}/pay/knet`;
  }

  INSTALLMENT_PAY_APPLE() {
    return `${this.envService?.ApiUrl}/accounts/payment/payInstallment/applepay`;
  }

  PAYMENT_STATUS(chargeId: string) {
    return `${this.envService?.ApiUrl}/accounts/payment/status/${chargeId}`;
  }

  RETRIEVE_CHARGE(chargeId: string) {
    return `${this.envService?.ApiUrl}/accounts/profile/retrieve-charge/${chargeId}`;
  }

  ORDER_STATUS(orderToken: string) {
    return `${this.envService?.ApiUrl}/accounts/payment/order/status/${orderToken}`;
  }

  GET_FAQ_LIST(category: string) {
    return `${this.envService?.ApiUrl}/accounts/faq?category=${category}`;
  }

  GET_POLICY(type: string) {
    return `${this.envService?.ApiUrl}/backoffice/taly-policy?type=${type}`;
  }

  GET_MERCHANT(pageParam?: string) {
    return `${this.envService?.ApiUrl}/merchants/merchantProfile/v2/limited?${pageParam}`;
  }

  GET_CATEGORY() {
    return `${this.envService?.ApiUrl}/backoffice/category`;
  }

  GET_COUNTRY() {
    return `${this.envService?.ApiUrl}/backoffice/country`;
  }

  GET_BUSINESS_TYPE() {
    return `${this.envService?.ApiUrl}/backoffice/businessType`;
  }

  GET_ANNUAL_SALES() {
    return `${this.envService?.ApiUrl}/backoffice/annualSale`;
  }

  GET_AVERAGE_ORDER_VALUE() {
    return `${this.envService?.ApiUrl}/backoffice/averageOrderValue`;
  }

  GET_PLATFORM() {
    return `${this.envService?.ApiUrl}/backoffice/platform`;
  }

  MERCHANT_INFO() {
    return `${this.envService?.ApiUrl}/merchants/interestRequest`;
  }
  MERCHANT_INFO_V2() {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/submit`;
  }

  GET_USER_PROFILE() {
    return `${this.envService?.ApiUrl}/accounts/profile`;
  }

  GET_ALL_PRESS_RELEASE() {
    return `${this.envService?.ApiUrl}/backoffice/press-release/all?isVisible=1`;
  }

  GET_PRESS_RELEAS_BY_ID(id: number) {
    return `${this.envService?.ApiUrl}/backoffice/press-release/${id}`;
  }

  CREATE_SESSION(orderId: number) {
    const orderQuery = orderId ? `?orderId=${orderId}` : '';

    return `${this.envService?.ApiUrl}/accounts/profile/creditCard/transaction/initiate${orderQuery}`;
  }

  // TODO: Uncomment to enable MyFatoorah
  // CREATE_SESSION(orderId: number) {
  //   const orderQuery = orderId ? `?orderId=${orderId}` : '';

  //   return `${this.envService?.ApiUrl}/accounts/profile/creditCard/v3/transaction/initiate${orderQuery}`;
  // }

  RESULT_INDICATOR_STATUS(resultIndicator: string) {
    return `${this.envService?.ApiUrl}/accounts/profile/creditCard/transaction/status?resultIndicator=${resultIndicator}`;
  }

  MPGS_SESSION_FAILURE(sessionId: string) {
    return `${this.envService?.ApiUrl}/accounts/profile/creditCard/transaction/failure?sessionId=${sessionId}`;
  }

  GET_TRANSACTION_STATUS() {
    return `${this.envService?.ApiUrl}/accounts/profile/creditCard/transaction/status`;
  }

  // TODO: Uncomment to enable MyFatoorah
  // GET_TRANSACTION_STATUS_V2(paymentId: string) {
  //   return `${this.envService?.ApiUrl}/accounts/profile/creditCard/v3/transaction/status?paymentId=${paymentId}`;
  // }

  VERIFY_CREDIT_CARD(sessionId: string) {
    return `${this.envService?.ApiUrl}/accounts/profile/creditCard/verify?sessionId=${sessionId}`;
  }

  // TODO: Uncomment to enable MyFatoorah
  // VERIFY_CREDIT_CARD_V2(sessionId: string) {
  //   return `${this.envService?.ApiUrl}/accounts/profile/creditCard/v2/verify?sessionId=${sessionId}`;
  // }

  GET_MPGS_CREDITCARD() {
    return `${this.envService?.ApiUrl}/accounts/profile/creditCard`;
  }

  POST_APPLE_PAY() {
    return `${this.envService?.ApiUrl}/accounts/payment/v2/payInstallment/applepay`;
  }

  POST_APPLE_PAY_STANDALONE(paymentToken: string) {
    return `${this.envService?.ApiUrl}/accounts/api/payment-links/${paymentToken}/pay/applePay`;
  }

  POST_APPLE_PAY_SESSION() {
    return `${this.envService?.ApiUrl}/accounts/profile/applepay/initiate/session`;
  }

  VALIDATE_ORDER_VALUE(orderId: string) {
    return `${this.envService?.ApiUrl}/accounts/payment/validate-order-value/${orderId}`;
  }

  CHECK_SYSTEM_AVAILABILITY() {
    return `${this.envService?.ApiUrl}/accounts/maintenance`;
  }

  // Document upload for interest request
  UPLOAD_DOCUMENTS(interestRequestId: string) {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/${interestRequestId}/documents`;
  }

  // Get documents for interest request
  GET_INTEREST_REQUEST_DOCUMENTS(interestRequestId: string) {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/${interestRequestId}/documents`;
  }

  // Delete document from interest request
  DELETE_INTEREST_REQUEST_DOCUMENT(interestRequestId: string, documentId: number) {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/${interestRequestId}/documents/${documentId}`;
  }

  // Token-based application completion
  GET_APPLICATION_BY_TOKEN(token: string) {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/application/${token}`;
  }

  COMPLETE_APPLICATION(token: string) {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/application/${token}/complete`;
  }

  // Get documents by token (public endpoint for consumer flow)
  GET_DOCUMENTS_BY_TOKEN(token: string) {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/application/${token}/documents`;
  }

  // Update interest request by ID (for 3-step signup flow)
  UPDATE_INTEREST_REQUEST(interestRequestId: number) {
    return `${this.envService?.ApiUrl}/merchants/interestRequest/${interestRequestId}`;
  }

  TOKEN_TYPE(tokenType: 'orderToken' | 'paymentToken', token: string) {
    return `${this.envService?.ApiUrl}/accounts/payment/transaction/info?${tokenType}=${token}`;
  }

  CNET_RENEWAL_AUTHENTICATION() {
    return `${this.envService?.ApiUrl}/accounts/profile/cinet/authentication/renewal`;
  }

  GET_CNET_RENEWAL_STATUS(referenceNo?: string) {
    let url = `${this.envService?.ApiUrl}/accounts/profile/cinet/authentication/renewal/status`;
    if (referenceNo) {
      url += `?referenceNo=${referenceNo}`;
    }
    return url;
  }

  AUTHENTICATE_CIVIL_ID(civilId: string) {
    return `${this.envService?.ApiUrl}/accounts/profile/authenticate?nationalId=${civilId}`;
  }

  REQUEST_CIVIL_ID_STATUS(civilId: string) {
    return `${this.envService?.ApiUrl}/accounts/profile/status?nationalId=${civilId}`;
  }

  PAY_LATER() {
    return `${this.envService?.ApiUrl}/accounts/payment/processOrderPayment`;
  }
  AUTHENTICATE_LOCKED_ACCOUNT() {
    return `${this.envService?.ApiUrl}/accounts/profile/authenticate`;
  }
  LOCKED_ACCOUNT_STATUS() {
    return `${this.envService?.ApiUrl}/accounts/profile/status`;
  }
  COMPLETE_ACCOUNT_FLOW() {
    return `${this.envService?.ApiUrl}/accounts/profile/flow/complete`;
  }
  CHANGE_LANGUAGE_PREFERENCE(lang: string) {
    return `${this.envService?.ApiUrl}/accounts/profile/changeLanguage?lang=${lang}`;
  }

  CHANGE_PHONE_NUMBER_AUTHENTICATE() {
    return `${this.envService?.ApiUrl}/accounts/profile/authenticate`;
  }

  CHANGE_PHONE_NUMBER_STATUS() {
    return `${this.envService?.ApiUrl}/accounts/profile/status`;
  }

  CHANGE_PHONE_NUMBER_LOGIN() {
    return `${this.envService?.ApiUrl}/accounts/profile/flow/complete`;
  }

  CHANGE_PHONE_NUMBER_OTP() {
    return `${this.envService?.ApiUrl}/accounts/profile/flow/complete`;
  }

  RESEND_CHANGE_NUMBER_OTP() {
    return `${this.envService?.ApiUrl}/accounts/profile/flow/send-sms-otp`;
  }

  POLICY_ACCEPTANCE(orderId: string) {
    return `${this.envService?.ApiUrl}/accounts/policy-acceptance?orderId=${orderId}`;
  }
}
